import {
    ReactElement,
    ReactNode,
    useState,
    FormEventHandler,
    useRef,
    useMemo,
    useCallback,
    useEffect
} from 'react';
import { CompanyUser } from '@/api';
import {
    useAddCompanyUserPhoneAction,
    useDeleteCompanyUserPhoneAction,
    useVerifyCompanyUserPhoneAction
} from '../actions';
import {
    UIModal,
    UIButton,
    UIFormInput,
    UIInlineAlert,
    UIPhoneInput,
    UIPhoneInputRef,
    UISection,
    UISectionContent,
    UISectionHeader,
    UIText,
    useUIConfirmationModal,
    UIFormInputRef,
    UIModalContent,
    UIModalFooter,
    UIModalHeader
} from '@/ui';
import { sanitisePhone } from '@/common/util';
import { useRegion } from '@/common/providers/region-provider';
import { formatPhone } from '../util/format-phone';
import { useCompaniesTranslation } from '../hooks';
import { useInterval } from '@/common/hooks';
import styles from './member-phone-settings-section.module.scss';

/**
 * Which of the member's two numbers a row is showing — `primary` is `phone`,
 * `secondary` is `phone2`. The two behave identically: add, verify by SMS
 * code, delete.
 */
type PhoneSlot = 'primary' | 'secondary';

type PhoneSettingsSectionProps = {
    companyUser: CompanyUser;
    canEdit: boolean;
    /** Explains why the section is read-only when `canEdit` is false. */
    editDisabledReason?: string;
    /**
     * Whether the email of the user *doing* the editing is verified — adding a
     * phone is blocked until it is. On an admin editing someone else, this is
     * still the admin's own flag, not the target user's.
     */
    isEmailVerified: boolean;
};

export function MemberPhoneSettingsSection(
    props: PhoneSettingsSectionProps
): ReactElement {
    const { companyUser, canEdit, editDisabledReason, isEmailVerified } = props;

    const { t } = useCompaniesTranslation();

    // A phone is only stored against the company user once it has been
    // verified, so the number being verified — and the row it came from — is
    // held here until then.
    const [pending, setPending] = useState<{
        slot: PhoneSlot;
        phone: string;
    } | null>(null);

    // Each row keeps the typed number in its own state, and stays mounted
    // behind the modal. Bumping a row's key remounts it to clear that number.
    const [formKeys, setFormKeys] = useState({ primary: 0, secondary: 0 });

    // The endpoint is fixed for a hook's lifetime, so each row gets its own
    // three. Indexed by slot below so the modal can reach whichever row opened
    // it.
    const addPrimary = useAddCompanyUserPhoneAction({ slot: 'primary' });
    const addSecondary = useAddCompanyUserPhoneAction({ slot: 'secondary' });
    const verifyPrimary = useVerifyCompanyUserPhoneAction({
        slot: 'primary',
        successMessage: t(
            'components.member_phone_settings_section.toast_primary_verified'
        )
    });
    const verifySecondary = useVerifyCompanyUserPhoneAction({
        slot: 'secondary',
        successMessage: t(
            'components.member_phone_settings_section.toast_secondary_verified'
        )
    });
    const deletePrimary = useDeleteCompanyUserPhoneAction({
        slot: 'primary',
        successMessage: t(
            'components.member_phone_settings_section.toast_primary_deleted'
        )
    });
    const deleteSecondary = useDeleteCompanyUserPhoneAction({
        slot: 'secondary',
        successMessage: t(
            'components.member_phone_settings_section.toast_secondary_deleted'
        )
    });

    const actions = {
        primary: {
            add: addPrimary,
            verify: verifyPrimary,
            delete: deletePrimary
        },
        secondary: {
            add: addSecondary,
            verify: verifySecondary,
            delete: deleteSecondary
        }
    };

    // Remounting a row is what clears the number typed into it and puts it
    // back into its read-only state — the row, not this component, owns both.
    function resetRow(slot: PhoneSlot) {
        setFormKeys((keys) => ({ ...keys, [slot]: keys[slot] + 1 }));
    }

    function onAddPhone(slot: PhoneSlot, phone: string) {
        actions[slot].add
            .addPhone(companyUser.id, { phone })
            .then(() => setPending({ slot, phone }))
            .catch(() => {}); // Error handled in the action
    }

    function onResendCode() {
        if (!pending) return;

        actions[pending.slot].add
            .addPhone(companyUser.id, { phone: pending.phone })
            .catch(() => {}); // Error handled in the action
    }

    function onVerifyPhone(data: { verificationCode: string }) {
        if (!pending) return;

        const { slot } = pending;
        const { verificationCode } = data;
        actions[slot].verify
            .verifyPhone(companyUser.id, {
                phone: pending.phone,
                code: verificationCode
            })
            .then(() => {
                // The number is stored now, so the row has to leave the entry
                // form it is still showing behind the modal.
                resetRow(slot);
                setPending(null);
            })
            .catch(() => {}); // Error handled in the action
    }

    function onCancelVerifyPhone() {
        if (pending) {
            resetRow(pending.slot);
        }
        setPending(null);
    }

    function onDeletePhone(slot: PhoneSlot) {
        actions[slot].delete.deletePhone(companyUser.id).catch(() => {}); // Error handled in the action
    }

    return (
        <>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                {canEdit && !isEmailVerified && (
                    <UIInlineAlert
                        text={t(
                            'components.member_phone_settings_section.email_not_verified'
                        )}
                    />
                )}
                <UISection>
                    <UISectionHeader
                        title={t(
                            'components.member_phone_settings_section.title'
                        )}
                    />
                    <UISectionContent>
                        <div
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 24
                            }}
                        >
                            <PhoneSlotRow
                                key={`primary-${formKeys.primary}`}
                                slot="primary"
                                phone={companyUser.phone}
                                canEdit={canEdit}
                                editDisabledReason={editDisabledReason}
                                isEmailVerified={isEmailVerified}
                                addLoading={addPrimary.loading}
                                deleteLoading={deletePrimary.loading}
                                onAddPhone={onAddPhone}
                                onDeletePhone={onDeletePhone}
                            />
                            <PhoneSlotRow
                                key={`secondary-${formKeys.secondary}`}
                                slot="secondary"
                                phone={companyUser.phone2}
                                canEdit={canEdit}
                                editDisabledReason={editDisabledReason}
                                isEmailVerified={isEmailVerified}
                                addLoading={addSecondary.loading}
                                deleteLoading={deleteSecondary.loading}
                                onAddPhone={onAddPhone}
                                onDeletePhone={onDeletePhone}
                            />
                        </div>
                    </UISectionContent>
                </UISection>
            </div>
            <UIModal isOpen={pending !== null} onClose={onCancelVerifyPhone}>
                <MemberVerifyPhoneModalForm
                    isOpen={pending !== null}
                    phone={pending?.phone ?? ''}
                    verifyLoading={
                        pending ? actions[pending.slot].verify.loading : false
                    }
                    resendLoading={
                        pending ? actions[pending.slot].add.loading : false
                    }
                    onClose={onCancelVerifyPhone}
                    onResend={onResendCode}
                    onSubmit={onVerifyPhone}
                />
            </UIModal>
        </>
    );
}

type PhoneSlotRowProps = {
    slot: PhoneSlot;
    phone: string | null;
    canEdit: boolean;
    /** Explains why the row is read-only when `canEdit` is false. */
    editDisabledReason?: string;
    isEmailVerified: boolean;
    addLoading: boolean;
    deleteLoading: boolean;
    onAddPhone: (slot: PhoneSlot, phone: string) => void;
    onDeletePhone: (slot: PhoneSlot) => void;
};

/**
 * One of the two numbers: the stored value with a Delete button, or an empty
 * row that turns into an entry form when Edit is pressed. There is no edit
 * path for a stored number — changing one is a delete followed by an add,
 * because the new number has to be verified before it replaces anything.
 */
function PhoneSlotRow(props: PhoneSlotRowProps): ReactElement {
    const {
        slot,
        phone,
        canEdit,
        editDisabledReason,
        isEmailVerified,
        addLoading,
        deleteLoading,
        onAddPhone,
        onDeletePhone
    } = props;

    const { t } = useCompaniesTranslation();
    const { resolvedRegion } = useRegion();
    const confirmationModal = useUIConfirmationModal();

    const [isEditing, setIsEditing] = useState(false);
    const [showRealTimeErrors, setShowRealTimeErrors] = useState(false);

    /**
     * Phone
     */
    const phoneInputRef = useRef<UIPhoneInputRef>(null);
    const defaultDialCode = resolvedRegion === 'us' ? '+1' : '+44';
    const lockDialCode = resolvedRegion === 'us' ? '+1' : undefined;
    const [dialCode, setDialCode] = useState(defaultDialCode);
    const [number, setNumber] = useState('');
    const [isPhoneValid, setIsPhoneValid] = useState(false);

    // Literal keys rather than one interpolated by slot: the i18n checker only
    // sees the static part of a dynamic key, and this set is fixed at two.
    const label =
        slot === 'secondary'
            ? t('components.member_phone_settings_section.secondary_label')
            : t('components.member_phone_settings_section.primary_label');
    const description =
        slot === 'secondary'
            ? t(
                  'components.member_phone_settings_section.secondary_description'
              )
            : t('components.member_phone_settings_section.primary_description');

    const handleSubmit: FormEventHandler = (e) => {
        e.preventDefault();

        setShowRealTimeErrors(true);

        if (isPhoneValid) {
            setShowRealTimeErrors(false);
            onAddPhone(slot, sanitisePhone(`${dialCode}${number}`));
        }
    };

    function resetForm() {
        setNumber('');
        phoneInputRef?.current?.resetForm();
        setShowRealTimeErrors(false);
        setIsEditing(false);
    }

    function openDeleteConfirmation() {
        confirmationModal.open({
            title:
                slot === 'secondary'
                    ? t(
                          'components.member_phone_settings_section.delete_secondary_confirm_title'
                      )
                    : t(
                          'components.member_phone_settings_section.delete_primary_confirm_title'
                      ),
            confirmText: t(
                'components.member_phone_settings_section.delete_confirm_button'
            ),
            confirmVariant: 'danger',
            cancelText: t('shared.labels.cancel'),
            onConfirm: () => {
                confirmationModal.close();
                onDeletePhone(slot);
            },
            onCancel: () => {
                confirmationModal.close();
            }
        });
    }

    const phoneString = useMemo<string>(
        () => (phone ? formatPhone(phone) : ''),
        [phone]
    );

    if (isEditing) {
        return (
            <form onSubmit={handleSubmit} noValidate>
                <PhoneRow
                    label={label}
                    description={description}
                    actions={
                        <>
                            <UIButton
                                variant="primary"
                                type="submit"
                                loading={addLoading}
                                disabled={addLoading}
                                text={
                                    addLoading
                                        ? t('shared.labels.saving')
                                        : t('shared.labels.save')
                                }
                            />
                            <UIButton
                                type="button"
                                disabled={addLoading}
                                text={t('shared.labels.cancel')}
                                onClick={resetForm}
                            />
                        </>
                    }
                >
                    <UIPhoneInput
                        ref={phoneInputRef}
                        name="phone"
                        // `initial*` seed the input's own state, and its
                        // resetForm() restores them — so they must stay
                        // the empty starting values, not the live ones.
                        initialDialCode={defaultDialCode}
                        initialNumber={''}
                        lockDialCode={lockDialCode}
                        showRealTimeErrors={showRealTimeErrors}
                        disabled={!isEmailVerified}
                        style={{ flex: 1 }}
                        onChange={(newDialCode, newNumber, isValid) => {
                            setDialCode(newDialCode);
                            setNumber(newNumber);
                            setIsPhoneValid(isValid);
                        }}
                    />
                </PhoneRow>
            </form>
        );
    }

    return (
        <PhoneRow
            label={label}
            description={description}
            actions={
                phone ? (
                    <UIButton
                        variant="textDanger"
                        type="button"
                        loading={deleteLoading}
                        disabled={!canEdit}
                        disabledTooltip={editDisabledReason}
                        text={t(
                            'components.member_phone_settings_section.delete_button'
                        )}
                        onClick={openDeleteConfirmation}
                    />
                ) : (
                    <UIButton
                        variant="textDefault"
                        type="button"
                        disabled={!canEdit}
                        disabledTooltip={editDisabledReason}
                        text={t('shared.labels.edit')}
                        onClick={() => setIsEditing(true)}
                    />
                )
            }
        >
            <UIFormInput
                name="phone"
                value={phone ? phoneString : t('shared.labels.none')}
                disabled={true}
            />
        </PhoneRow>
    );
}

/**
 * The label/description column, with whatever input and buttons belong beside
 * it. Stacks to one column on a narrow screen — see the stylesheet.
 */
function PhoneRow(props: {
    label: string;
    description: string;
    children: ReactElement;
    actions: ReactNode;
}): ReactElement {
    const { label, description, children, actions } = props;

    return (
        <div className={styles.row}>
            <div className={styles.label}>
                <UIText>{label}</UIText>
                <UIText type="default2">{description}</UIText>
            </div>
            <div className={styles.value}>
                <div className={styles.field}>{children}</div>
                <div className={styles.actions}>{actions}</div>
            </div>
        </div>
    );
}

/** How long the user must wait between requesting verification codes. */
const RESEND_COOLDOWN_SECONDS = 60;

type MemberVerifyPhoneModalFormProps = {
    isOpen: boolean;
    phone: string;
    verifyLoading: boolean;
    resendLoading: boolean;
    onClose: () => void;
    onResend: () => void;
    onSubmit: (data: { verificationCode: string }) => void;
};

function MemberVerifyPhoneModalForm(
    props: MemberVerifyPhoneModalFormProps
): ReactElement {
    const { isOpen } = props;

    if (isOpen) {
        return <FormContent {...props} />;
    } else {
        return <></>;
    }
}

function FormContent(props: MemberVerifyPhoneModalFormProps): ReactElement {
    const {
        isOpen,
        phone,
        verifyLoading,
        resendLoading,
        onClose,
        onResend,
        onSubmit
    } = props;

    const { t } = useCompaniesTranslation();

    const [showRealTimeErrors, setShowRealTimeErrors] = useState(false);

    /**
     * Verification code
     */
    const verificationCodeRef = useRef<UIFormInputRef>(null);
    const [verificationCode, setVerificationCode] = useState('');
    const [verificationCodeError, setVerificationCodeError] = useState('');
    const checkVerificationCode = useCallback(
        (verificationCode: string): string => {
            const trimmed = verificationCode.trim();
            if (trimmed.length !== 6) {
                return t(
                    'components.member_phone_settings_section.verify_code_error_length'
                );
            } else if (!/^\d+$/.test(trimmed)) {
                return t(
                    'components.member_phone_settings_section.verify_code_error_not_a_number'
                );
            }
            return '';
        },
        [t]
    );
    useEffect(() => {
        if (showRealTimeErrors) {
            setVerificationCodeError(checkVerificationCode(verificationCode));
        }
    }, [verificationCode, showRealTimeErrors, checkVerificationCode]);

    const handleSubmit: FormEventHandler = (e) => {
        e.preventDefault();

        setShowRealTimeErrors(true);

        if (!checkVerificationCode(verificationCode)) {
            setShowRealTimeErrors(false);
            onSubmit?.({ verificationCode });
        }
    };

    useEffect(() => {
        if (isOpen) {
            verificationCodeRef.current?.focus();
        }
    }, [isOpen]);

    /**
     * Resend cooldown. A code was sent to open this modal, so the countdown
     * starts straight away and restarts on every resend.
     */
    const [secondsUntilResend, setSecondsUntilResend] = useState(
        RESEND_COOLDOWN_SECONDS
    );
    useInterval(() => {
        setSecondsUntilResend((seconds) => (seconds > 0 ? seconds - 1 : 0));
    }, 1000);

    function handleResend() {
        setSecondsUntilResend(RESEND_COOLDOWN_SECONDS);
        onResend();
    }

    const loading = verifyLoading || resendLoading;

    let resendCodeText = t(
        'components.member_phone_settings_section.resend_button'
    );
    if (resendLoading) {
        resendCodeText = t(
            'components.member_phone_settings_section.resend_sending'
        );
    } else if (secondsUntilResend > 0) {
        resendCodeText = t(
            'components.member_phone_settings_section.resend_countdown',
            {
                seconds: secondsUntilResend
            }
        );
    }

    return (
        <form onSubmit={handleSubmit} noValidate>
            <UIModalHeader
                title={t(
                    'components.member_phone_settings_section.verify_title'
                )}
                subtitle={t(
                    'components.member_phone_settings_section.verify_subtitle'
                )}
            />
            <UIModalContent>
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 16
                    }}
                >
                    <UIText>
                        {t(
                            'components.member_phone_settings_section.verify_code_sent'
                        )}{' '}
                        <UIText bold>{formatPhone(phone)}</UIText>
                    </UIText>
                    <UIFormInput
                        ref={verificationCodeRef}
                        name="Verification Code"
                        value={verificationCode}
                        placeholder={t(
                            'components.member_phone_settings_section.verify_code_placeholder'
                        )}
                        type="text"
                        errorText={verificationCodeError}
                        required
                        maxLength={6}
                        onChange={setVerificationCode}
                        autoFocus
                    />
                </div>
            </UIModalContent>
            <UIModalFooter
                actions={[
                    {
                        variant: 'primary',
                        type: 'submit',
                        loading: verifyLoading,
                        disabled: loading,
                        text: verifyLoading
                            ? t(
                                  'components.member_phone_settings_section.verifying_button'
                              )
                            : t(
                                  'components.member_phone_settings_section.verify_button'
                              )
                    },
                    {
                        text: resendCodeText,
                        type: 'button',
                        loading: resendLoading,
                        disabled: loading || secondsUntilResend > 0,
                        onClick: handleResend
                    },
                    {
                        text: t('shared.labels.cancel'),
                        type: 'button',
                        disabled: loading,
                        onClick: onClose
                    }
                ]}
            />
        </form>
    );
}
